import { cloneDeep, isEqual, isObjectLike } from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataType = Record<string, any>;

export class SessionItemData {

	private _data: DataType;
	private _data$: BehaviorSubject<DataType>;

	constructor(data: DataType = {}) {
		if (!isObjectLike(data)) {
			throw Error('data is not object-like');
		}
		this._data = cloneDeep(data);
		this._data$ = new BehaviorSubject(this._data);
	}

	private parseProperty(property: string): string[] {
		if (!property)
			return [];

		return property.split(/\./);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getProperty(property: string | string[]): any {
		if (typeof property === 'string') {
			property = this.parseProperty(property as string);
		}
		const path = [].concat(property);
		let data = this._data;
		while (path.length && data !== undefined) {
			const key = path.shift();

			if (typeof data === 'object') {
				data = data[key];
			} else {
				console.warn(`requesting key ${key} from`, data);
				data = undefined;
			}
		}
		return data;
	}

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
	setProperty(property: string | string[], value: any): void {
		if (typeof property === 'string') {
			property = this.parseProperty(property as string);
		}
		const path = [].concat(property);
		let data = this._data;
		while (path.length) {
			const key = path.shift();
			if (typeof data === 'object') {
				if (path.length === 0) {
					data[key] = value;
					this._data$.next(this._data);
				} else {
					if (data[key] === undefined) {
						data[key] = {};
					}
					data = data[key];
				}
			} else {
				console.warn(`requesting key ${key} from`, data);
				data = undefined;
			}
		}
	}

	/**
	 * Observable for a property
	 *
	 * Fires only if the property value changes (deep equal).
	 *
	 * @param property Property key
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	property$(property: string | string[]): Observable<any> {
		if (typeof property === 'string') {
			property = this.parseProperty(property as string);
		}
		return this._data$.pipe(
			map(() => this.getProperty(property)),
			distinctUntilChanged(isEqual),
		);
	}

	get data(): DataType {
		return this._data;
	}

	/**
	 * Observable for the session data.
	 */
	get data$(): Observable<DataType> {
		return this._data$;
	}
}
